import { RefreshCw, Trash2, AlertCircle, CheckCircle2, Clock, Loader2 } from 'lucide-react'
import { api } from '@/api/client'
import { useEffect, useState } from 'react'
import type { QueuedMutation } from '@/lib/sync-queue'
import { cn } from '@/lib/utils'

function timeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000)
  if (seconds < 60) return `${seconds}s ago`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  return `${hours}h ago`
}

const statusConfig: Record<string, { icon: typeof Clock; color: string; label: string }> = {
  pending: { icon: Clock, color: 'text-amber-500', label: 'Pending' },
  syncing: { icon: Loader2, color: 'text-blue-500', label: 'Syncing' },
  failed: { icon: AlertCircle, color: 'text-red-500', label: 'Failed' },
}

interface SyncStatusPanelProps {
  onClose: () => void
}

/**
 * Expandable panel showing queued mutations with retry/discard actions.
 * Triggered from the ApiStatusBar badge.
 */
export function SyncStatusPanel({ onClose }: SyncStatusPanelProps) {
  const [mutations, setMutations] = useState<QueuedMutation[]>([])

  useEffect(() => {
    const refresh = () => setMutations([...api.syncQueue.all()])
    refresh()

    // Refresh on status changes (which fire during drain)
    api.onStatusChange(refresh)
    return () => api.offStatusChange(refresh)
  }, [])

  const handleRetry = (id: string) => {
    api.syncQueue.retry(id)
    setMutations([...api.syncQueue.all()])
  }

  const handleDiscard = (id: string) => {
    api.syncQueue.discard(id)
    setMutations([...api.syncQueue.all()])
  }

  if (mutations.length === 0) {
    return (
      <div className="absolute right-0 top-full z-50 mt-1 w-80 rounded-lg border border-border bg-popover p-4 shadow-lg">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          All changes synced
        </div>
      </div>
    )
  }

  return (
    <div className="absolute right-0 top-full z-50 mt-1 w-96 rounded-lg border border-border bg-popover shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-2">
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          Pending Changes ({mutations.length})
        </span>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          Close
        </button>
      </div>

      {/* Mutation list */}
      <div className="max-h-64 overflow-y-auto">
        {mutations.map((mutation) => {
          const config = statusConfig[mutation.status] ?? statusConfig.pending
          const Icon = config.icon

          return (
            <div
              key={mutation.id}
              className="flex items-start gap-3 border-b border-border/50 px-4 py-2.5 last:border-0"
            >
              <Icon
                className={cn(
                  'mt-0.5 h-3.5 w-3.5 shrink-0',
                  config.color,
                  mutation.status === 'syncing' && 'animate-spin',
                )}
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium text-foreground">
                  {mutation.description}
                </p>
                <div className="mt-0.5 flex items-center gap-2 text-[10px] text-muted-foreground">
                  <span>{config.label}</span>
                  <span>·</span>
                  <span>{timeAgo(mutation.queuedAt)}</span>
                  {mutation.retries > 0 && (
                    <>
                      <span>·</span>
                      <span>{mutation.retries} retries</span>
                    </>
                  )}
                </div>
                {mutation.error && (
                  <p className="mt-1 text-[10px] text-red-500 dark:text-red-400 line-clamp-2">
                    {mutation.error}
                  </p>
                )}
              </div>

              {/* Actions for failed mutations */}
              {mutation.status === 'failed' && (
                <div className="flex shrink-0 gap-1">
                  <button
                    type="button"
                    onClick={() => handleRetry(mutation.id)}
                    className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                    title="Retry"
                  >
                    <RefreshCw className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDiscard(mutation.id)}
                    className="rounded p-1 text-muted-foreground hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30"
                    title="Discard"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
